import { Col, Input, Row, Select } from 'antd'
import { GraviButton, Texto } from '@gravitate-js/excalibrr'
import { SingleFlag } from '@components/_SiteWide/Flags/FlagIcon'
import React, { useEffect, useState } from 'react'
import { rowStyles, actionRowStyle, hrStyle, CPH_FLAG } from '@components/_SiteWide/Flags/constants'
import { animated, useTransition } from 'react-spring'
import { LoadingOutlined } from '@ant-design/icons'
import type { InferResponse } from '@/hooks/useApiClient'
const { TextArea } = Input

export type ReasonCodeResponse = InferResponse<'/order/reason_code/list'>

type ResolveFlagFormProps = {
  flag: Gravitate.OrderFlag
  reason: string | null
  setReason: (reason: string | null) => void
  notes: string
  setNotes: (notes: string) => void
  setShowResolveForm: (show: boolean) => void
  resolveFlag: () => void
  showResolveForm: boolean
  loading: boolean
  setLoading: (loading: boolean) => void
  reasonCodes: ReasonCodeResponse['supply_options']
}

export function ResolveFlagForm({
  flag,
  reason,
  setReason,
  notes,
  setNotes,
  setShowResolveForm,
  resolveFlag,
  showResolveForm,
  loading,
  setLoading,
  reasonCodes,
}: ResolveFlagFormProps): JSX.Element {
  const [error, setError] = useState(false)
  const [errorMessage, setErrorMessage] = useState({ code: 0, message: '' })

  const hideAndResetForm = () => {
    setReason(null)
    setNotes('')
    setLoading(false)
    setShowResolveForm(false)
  }

  const resolveFlagsCheck = () => {
    setLoading(true)
    if (flag.type !== CPH_FLAG && !reason) {
      setError(true)
      setErrorMessage({ code: 1, message: 'Please add a Reason' })
      setLoading(false)
      return
    }
    if (reason === 'Other' && !notes) {
      setError(true)
      setErrorMessage({ code: 2, message: 'Please add a Note if the Reason is Other' })
      setLoading(false)
      return
    }
    if (!error) {
      return resolveFlag()
    }
  }

  const transitions = useTransition(showResolveForm, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    reverse: !showResolveForm,
    config: { duration: 200 },
  })
  return transitions(
    (styles, item) =>
      item && (
        <animated.div style={styles}>
          <Row className='my-4' style={rowStyles}>
            <Col className='flex-div' flex={1}>
              <Texto>Resolving:</Texto>
            </Col>
            <Col className='flex-div'>
              <SingleFlag small flag={flag} />
              <Texto
                align='center'
                weight='bold'
                className='ml-3'
                style={{ flexWrap: 'nowrap' }}
                appearance={flag.severity}
              >
                {flag.name}
              </Texto>
            </Col>
          </Row>
          <hr style={hrStyle} />
          {flag?.type !== CPH_FLAG && (
            <Row className='my-4' style={rowStyles}>
              <Col flex={1}>
                <Texto className='pt-2 pr-3'>Reason</Texto>
              </Col>
              <Col>
                <ReasonSelect
                  value={reason}
                  onChange={(e) => setReason(e)}
                  setError={setError}
                  error={error}
                  reasonCodes={reasonCodes}
                />
                {error && errorMessage.code === 1 && <Texto appearance='warning'>{errorMessage.message} </Texto>}
              </Col>
            </Row>
          )}
          <Row className='mt-3 mb-1' style={rowStyles}>
            <TextArea
              onFocus={() => {
                if (error) setError(false)
              }}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder='Enter Resolution Notes'
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{ minWidth: 300, width: '100%' }}
            />
            {error && errorMessage.code === 2 && <Texto appearance='warning'>{errorMessage.message} </Texto>}
          </Row>
          <Row style={actionRowStyle}>
            <Col flex={1}>
              <GraviButton buttonText='Cancel' onClick={hideAndResetForm} />
            </Col>
            <Col>
              <GraviButton
                icon={loading && <LoadingOutlined spin />}
                disabled={loading}
                theme2
                buttonText='Resolve Flag'
                onClick={resolveFlagsCheck}
              />
            </Col>
          </Row>
        </animated.div>
      )
  )
}

type ReasonSelectProps = {
  value: string | null
  onChange: (value: string) => void
  setError: (error: boolean) => void
  error: boolean
  reasonCodes: { id: string; reason: string }[] | null
}

const ReasonSelect = ({ value, onChange, setError, error, reasonCodes }: ReasonSelectProps) => {
  return (
    <Select
      value={value}
      style={{ width: 300, marginRight: 0 }}
      onChange={onChange}
      onFocus={() => {
        if (error) setError(false)
      }}
    >
      {reasonCodes?.map((option) => {
        return (
          <Select.Option value={option.reason} key={option.id}>
            {option.reason}
          </Select.Option>
        )
      })}
    </Select>
  )
}
